import React, {FC, ReactNode} from 'react';

import {findTicketingContextForTeamStatus} from '../../../../../../../libs/inboxes/business/src/ticketing/teamStatus';
import {TicketStatusName} from '../../../../../../../libs/inboxes/ui/src/ticketing/ticketStatusName';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontEvent} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ConversationTeamStatusesEnum} from '../../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {TicketStatusModel} from '../../../../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {IconButton} from '../../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {
  Dropdown,
  DropdownInheritableProps,
} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownHeader} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownList} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemChevron} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemChevron';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {TicketStatusDropdownItemIcon} from '../../../../ticketing/ticketStatusDropdownItemIcon';

/*
 * Props.
 */

interface SnoozeTicketStatusDropdownProps extends DropdownInheritableProps {
  title: ReactNode;
  computeIsTicketStatusDisabled?: (ticketStatus: TicketStatusModel) => boolean;
  ticketStatuses: ReadonlyArray<TicketStatusModel>;
  onTicketStatusSelect: (event: FrontEvent, ticketStatus: TicketStatusModel) => void;
  onBackClick?: () => void;
  backLabel?: ReactNode;
}

/*
 * Component.
 */

export const SnoozeTicketStatusDropdown: FC<SnoozeTicketStatusDropdownProps> = (props) => {
  const {title, computeIsTicketStatusDisabled, ticketStatuses, onTicketStatusSelect, ...dropdownProps} =
    props;

  const archivedTicketingContext = findTicketingContextForTeamStatus(ConversationTeamStatusesEnum.ARCHIVED);
  const archivedTicketingStatuses = ticketStatuses.filter((ticketStatus) =>
    archivedTicketingContext.eligibleCategories.includes(ticketStatus.category),
  );

  const maybeRenderHeaderLeftIcon = () => {
    if (!(props.onBackClick && props.backLabel)) {
      return null;
    }
    return (
      <IconButton
        iconName="chevronLeftSmall"
        label={props.backLabel}
        onClick={props.onBackClick}
        size={VisualSizesEnum.SMALL}
      />
    );
  };

  return (
    <Dropdown {...dropdownProps} minWidth={260}>
      <DropdownHeader title={title} leftIcon={maybeRenderHeaderLeftIcon()} />
      <DropdownList>
        <DropdownCategory>
          <Trans id="snooze-ticket-status-subheader">Status while snoozed</Trans>
        </DropdownCategory>

        {archivedTicketingStatuses.map((ticketStatus) => (
          <DropdownItem
            key={ticketStatus.tagId}
            onSelect={(event) => onTicketStatusSelect(event, ticketStatus)}
            isDisabled={computeIsTicketStatusDisabled?.(ticketStatus)}
            renderRightContent={() => <DropdownItemChevron />}
          >
            <TicketStatusDropdownItemIcon category={ticketStatus.category} />
            <DropdownItemContent>
              <TicketStatusName tagId={ticketStatus.tagId} />
            </DropdownItemContent>
          </DropdownItem>
        ))}
      </DropdownList>
    </Dropdown>
  );
};
